import { gql, useQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";

const TRY_TIMES_BEFORE_QUIT = 3;
const MILISECONDS_BETWEEN_TRIES = 3000;

export const GET_MY_PMX_ACTIVITIES_FAVORITES_QUERY = gql`
  query getMyPmxActivitiesFavorites {
    getMyPmxActivitiesFavorites {
      id
      trialNumber
      trialShortDescription
      bayNumber
      projectId
      mainRepository {
        isStandard
      }
    }
  }
`;

export function useGetMyPmxActivitiesFavoritesQuery() {
  const retryTimesRef = useRef(0);
  const refetchRef = useRef<() => void>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);

  const {
    data: { getMyPmxActivitiesFavorites: favoriteActivities } = {},
    loading: favoriteActivitiesLoading,
    error: errorFavoriteActivities,
    refetch: refetchFavoriteActivities
  } = useQuery(GET_MY_PMX_ACTIVITIES_FAVORITES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      retryTimesRef.current = 0;
      setIsRetrying(false);
      setHasExhaustedRetries(false);
    },
    onError() {
      // Retry up to 3 times before surfacing an error (Lambda cold starts can take ~10s)
      if (retryTimesRef.current < TRY_TIMES_BEFORE_QUIT) {
        setIsRetrying(true);
        timeoutRef.current = setTimeout(() => {
          retryTimesRef.current += 1;
          refetchRef.current?.();
        }, MILISECONDS_BETWEEN_TRIES);
      } else {
        setIsRetrying(false);
        setHasExhaustedRetries(true);
      }
    }
  });

  refetchRef.current = refetchFavoriteActivities;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    favoriteActivities,
    favoriteActivitiesLoading: favoriteActivitiesLoading || isRetrying,
    errorFavoriteActivities: hasExhaustedRetries
      ? errorFavoriteActivities
      : undefined,
    refetchFavoriteActivities,
    isRetrying
  };
}
